/** @format */

import { type QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { useEffect, useMemo } from "react";
import { HelmetProvider } from "react-helmet-async";

import { QUERY_CONFIG } from "./api/queryConfig";
import { RevalidationIndicator } from "./components/RevalidatingRacer";
import { QUERY_CACHE_SCHEMA_VERSION } from "./constants/version";
import { ConfigProvider } from "./context/configContext";
import LiveWrapper from "./LiveWrapper";
import { initializePerformanceMonitoring } from "./lib/performance";
import { queryClient } from "./queryClient";
import { createIDBPersister } from "./utils/queryPersister";

interface AppProps {
  queryClient?: QueryClient;
  ssrContext?: {
    routeMatch?: unknown;
  };
}

function App({ queryClient: providedQueryClient, ssrContext }: AppProps) {
  // Use provided queryClient or use the singleton
  // In SSR, we'll provide a queryClient with pre-fetched data
  // On client, we'll use the singleton queryClient
  const qc = providedQueryClient ?? queryClient;
  const isSSR = typeof window === "undefined";

  // Create IndexedDB persister for cache persistence (client-side only)
  // Memoize to avoid recreating on each render
  const persister = useMemo(() => (isSSR ? null : createIDBPersister()), [isSSR]);
  const showReactQueryDevtools =
    import.meta.env.DEV && import.meta.env.VITE_ENABLE_REACT_QUERY_DEVTOOLS === "true";

  // Initialize Web Vitals performance monitoring
  useEffect(() => {
    initializePerformanceMonitoring();
  }, []);

  const appContent = (
    <HelmetProvider>
      <ConfigProvider>
        <LiveWrapper ssrContext={ssrContext} queryClient={qc} isSSR={isSSR} />
      </ConfigProvider>
      {!isSSR && <RevalidationIndicator />}
      {showReactQueryDevtools && typeof window !== "undefined" && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </HelmetProvider>
  );

  // Use PersistQueryClientProvider on client for IndexedDB cache persistence
  // Use regular QueryClientProvider for SSR
  if (persister) {
    return (
      <PersistQueryClientProvider
        client={qc}
        persistOptions={{
          persister,
          maxAge: 1000 * 60 * 60 * 24, // 24 hours
          buster: `query-cache-v${QUERY_CACHE_SCHEMA_VERSION}`,
          dehydrateOptions: {
            shouldDehydrateQuery: query =>
              query.meta?.persist !== false && query.state.status === "success",
          },
        }}
        onSuccess={() => {
          // Invalidate persisted queries that would otherwise never revalidate
          // on hydration:
          //   - STATIC_DATA (30 min staleTime): may be stale across sessions.
          //   - PLAYBACK_DATA (Infinity staleTime, refetchOnMount: false): never
          //     refetches on its own; without this the user could view a whole
          //     run from IndexedDB with no background fetch, missing any
          //     correction that landed since last session.
          // WEBSOCKET_DATA and POLLED_LIVE_DATA are intentionally excluded —
          // their short staleTime already triggers a refetch on mount, and
          // invalidating them here would cause a duplicate fetch wave.
          // refetchType: "active" keeps the fan-out bounded to queries
          // mounted on the current screen.
          void qc.invalidateQueries({
            refetchType: "active",
            predicate: query => {
              const staleTime = (query.options as { staleTime?: number }).staleTime;
              return (
                staleTime === QUERY_CONFIG.STATIC_DATA.staleTime ||
                staleTime === QUERY_CONFIG.PLAYBACK_DATA.staleTime
              );
            },
          });
        }}
      >
        {appContent}
      </PersistQueryClientProvider>
    );
  }

  return <QueryClientProvider client={qc}>{appContent}</QueryClientProvider>;
}

export default App;
